import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import { toast } from "react-toastify";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import { suggestReplyWithAi, transformTextWithAi } from "../../services/ai";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.background.default,
    backgroundBlendMode: "multiply",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.inboundMessage,
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: darkModeBoxShadow(theme),
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.custom.inboundQuoted,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.light,
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.outboundMessage,
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: darkModeBoxShadow(theme),
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.custom.outboundQuoted,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.secondary.main,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.custom.actionIcon,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.light,
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.secondary,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.custom.infoBadge,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: darkModeBoxShadow(theme),
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  translatedMessageBox: {
    marginTop: 8,
    padding: "8px 10px",
    borderRadius: 10,
    backgroundColor: theme.custom.panelSoft,
    border: `1px solid ${theme.custom.border}`,
  },

  translatedMessageLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
}));

function darkModeBoxShadow(theme) {
  return theme.palette.type === "dark"
    ? "0 10px 24px rgba(15, 23, 42, 0.28)"
    : "0 1px 1px rgba(15, 23, 42, 0.18)";
}

const IMAGE_MEDIA_REGEX = /^.*\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const VIDEO_MEDIA_REGEX = /^.*\.(mp4|webm|ogg|mov)$/i;

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    const nextState = [...state];

    messages.forEach((message) => {
      const messageIndex = nextState.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        nextState[messageIndex] = {
          ...nextState[messageIndex],
          ...message
        };
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...nextState];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      const nextState = [...state];
      nextState[messageIndex] = {
        ...nextState[messageIndex],
        ...newMessage
      };
      return nextState;
    }

    return [...state, newMessage];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      const nextState = [...state];
      nextState[messageIndex] = {
        ...nextState[messageIndex],
        ...messageToUpdate
      };
      return nextState;
    }

    return state;
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const MessagesList = ({ ticketId, isGroup, onComposerAction }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const oldestMessageCursorRef = useRef(null);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [aiActionMessageId, setAiActionMessageId] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setTranslatedMessages({});
    oldestMessageCursorRef.current = null;

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const params = {};

          if (pageNumber > 1 && oldestMessageCursorRef.current) {
            params.before = oldestMessageCursorRef.current;
          } else {
            params.pageNumber = 1;
          }

          const { data } = await api.get("/messages/" + ticketId, {
            params,
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);

            if (data.messages?.length > 0) {
              oldestMessageCursorRef.current = data.messages[0].createdAt;
            }
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 120);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    let isCancelled = false;

    const syncLatestMessages = async () => {
      if (document.hidden) {
        return;
      }

      try {
        const { data } = await api.get(`/messages/${ticketId}`, {
          params: { pageNumber: 1 }
        });

        if (isCancelled || currentTicketId.current !== ticketId) {
          return;
        }

        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
      } catch (err) {
        // Silent fallback sync: keep realtime UX clean on transient failures.
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncLatestMessages();
      }
    };

    const handleFocus = () => {
      syncLatestMessages();
    };

    const handleOnline = () => {
      syncLatestMessages();
    };

    const syncInterval = setInterval(() => {
      syncLatestMessages();
    }, 7000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      isCancelled = true;
      clearInterval(syncInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, [ticketId]);

  useEffect(() => {
    const socket = openSocket();
    const joinChatRoom = () => socket.emit("joinChatBox", ticketId);
    const selectedTicketId = Number(ticketId);

    socket.on("connect", joinChatRoom);
    joinChatRoom();

    socket.on("appMessage", (data) => {
      const payloadTicketId = Number(
        data?.message?.ticketId ?? data?.ticket?.id
      );

      if (!payloadTicketId || payloadTicketId !== selectedTicketId) {
        return;
      }

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.emit("leaveChatBox", ticketId);
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const handleSuggestReply = async message => {
    if (aiActionMessageId) {
      return;
    }

    setAiActionMessageId(message.id);

    try {
      const data = await suggestReplyWithAi({
        text: message.body,
        targetLanguage: "Brazilian Portuguese (pt-BR)"
      });

      onComposerAction?.({
        id: `${message.id}:suggest:${Date.now()}`,
        type: "suggest_reply",
        text: data.text || "",
        replyingMessage: message
      });

      toast.success(i18n.t("messageOptionsMenu.suggestionReady"));
    } catch (err) {
      toastError(err);
    }

    setAiActionMessageId(null);
  };

  const handleTranslateMessage = async (message, targetLanguage) => {
    if (aiActionMessageId) {
      return;
    }

    setAiActionMessageId(message.id);

    try {
      const data = await transformTextWithAi({
        action: "translate",
        text: message.body,
        targetLanguage
      });

      setTranslatedMessages(prevState => ({
        ...prevState,
        [message.id]: {
          text: data.text || "",
          language: targetLanguage
        }
      }));
    } catch (err) {
      toastError(err);
    }

    setAiActionMessageId(null);
  };

  const handleHideTranslation = message => {
    setTranslatedMessages(prevState => {
      const nextState = { ...prevState };
      delete nextState[message.id];
      return nextState;
    });
  };

  const isImageMedia = (message) => {
    const mediaUrl = message.mediaUrl || "";

    return (
      message.mediaType === "image" ||
      message.mediaType === "sticker" ||
      IMAGE_MEDIA_REGEX.test(mediaUrl)
    );
  };

  const isVideoMedia = (message) => {
    const mediaUrl = message.mediaUrl || "";

    return message.mediaType === "video" || VIDEO_MEDIA_REGEX.test(mediaUrl);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (isImageMedia(message)) {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (isVideoMedia(message)) {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderTranslatedMessage = message => {
    const translatedMessage = translatedMessages[message.id];

    if (!translatedMessage?.text) {
      return null;
    }

    return (
      <div className={classes.translatedMessageBox}>
        <span className={classes.translatedMessageLabel}>
          {i18n.t("messageOptionsMenu.translatedLabel")}
        </span>
        <MarkdownWrapper>{translatedMessage.text}</MarkdownWrapper>
      </div>
    );
  };

  const renderedMessages = useMemo(() => {
    if (messagesList.length === 0) {
      return <div>Say hello to your new contact!</div>;
    }

    return messagesList.map((message, index) => {
      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div className={classes.messageLeft}>
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {isGroup && (
                <span className={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}
              {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                //|| message.mediaType === "multi_vcard"
              ) && checkMessageMedia(message)}
              <div className={classes.textContentItem}>
                {message.quotedMsg && renderQuotedMessage(message)}
                <MarkdownWrapper>{message.body}</MarkdownWrapper>
                {renderTranslatedMessage(message)}
                <span className={classes.timestamp}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderMessageDivider(message, index)}
          <div className={classes.messageRight}>
            <IconButton
              variant="contained"
              size="small"
              id="messageActionsButton"
              disabled={message.isDeleted}
              className={classes.messageActionsButton}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
            >
              <ExpandMore />
            </IconButton>
            {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
              //|| message.mediaType === "multi_vcard"
            ) && checkMessageMedia(message)}
            <div
              className={clsx(classes.textContentItem, {
                [classes.textContentItemDeleted]: message.isDeleted,
              })}
            >
              {message.isDeleted && (
                <Block
                  color="disabled"
                  fontSize="small"
                  className={classes.deletedIcon}
                />
              )}
              {message.quotedMsg && renderQuotedMessage(message)}
              <MarkdownWrapper>{message.body}</MarkdownWrapper>
              {renderTranslatedMessage(message)}
              <span className={classes.timestamp}>
                {format(parseISO(message.createdAt), "HH:mm")}
                {renderMessageAck(message)}
              </span>
            </div>
          </div>
        </React.Fragment>
      );
    });
  }, [messagesList, isGroup, classes, translatedMessages]);

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        onSuggestReply={handleSuggestReply}
        onTranslateMessage={handleTranslateMessage}
        onHideTranslation={handleHideTranslation}
        hasTranslation={Boolean(translatedMessages[selectedMessage.id])}
        isAiActionLoading={Boolean(aiActionMessageId)}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderedMessages}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
